import { DateTime } from 'luxon';

import { BusinessEntity, ListTemplate, Organization } from '../../types';
import { USD } from '../../types/usd';
import { dateSort, parseDate } from '../../utils/DateTime';
import { AddressComponent } from '../components/address.template';
import { BusinessEntityComponent, BusinessEntityTemplateType } from '../components/businessEntity.template';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyListMeta, formatCurrency, getPrimaryBusiness } from './formatters';

export type CustomerStatementInputType = {
  organization: Organization;
  businessEntity: BusinessEntity;
  bills: Array<{
    slug: string;
    amountDue: USD;
    timestamps: { invoiceDate: string | Date; dueDate: string | Date };
    receipts: Array<{ amount: USD }>;
    adjustments: Array<ReceiptAdjustment>;
    details: { reference: string; paymentTerms: string };
  }>;
};

type ReceiptAdjustment = { type: 'CREDIT' | 'DEBIT' | 'ADJUSTMENT'; amount: USD };

export const CustomerStatementTemplate: DocumentTypePopulator<
  CustomerStatementInputType,
  void,
  Partial<{
    invoices: ListTemplate<{
      invoice: string;
      date: string;
      referenceNumber?: string;
      dueDate: string;
      currentDays: number;
      overdueDays: number | string;
      amount: string;
      payments: string;
      openBalance: string;
    }>;
    email: string;
    customer: BusinessEntityTemplateType;
    phoneNumber: string;
    organization: OrganizationTemplateType;
    businessAddress: Record<string, string | undefined | boolean>;
    totalDue: string;
    totalPaid: string;
  }>
> = {
  populate: (data, _, globalOptions) => {
    if (!data) return {};

    const { organization, bills } = data;

    const calculateDays = (startDate?: string | Date | null, endDate?: string | null): number => {
      const start = parseDate(startDate);
      const end = endDate ? parseDate(endDate) : DateTime.local();

      if (!start || !end) return 0;

      return Math.floor(end.diff(start, 'days').days);
    };

    const allInvoices = [...bills]
      .sort((a, b) => dateSort(a.timestamps.invoiceDate, b.timestamps.invoiceDate))
      .map((bill) => {
        const { slug, receipts, amountDue: amountDueSerialized, details, adjustments, timestamps } = bill;

        const amountDue = USD.fromSerialized(amountDueSerialized) ?? USD.fromCents(0);
        const totalPaid = receipts.reduce(
          (acc, payment) => acc.add(USD.fromSerialized(payment.amount) ?? USD.fromCents(0)),
          USD.fromCents(0)
        );
        const totalAdjustments = adjustments.reduce(
          (acc, adjustment) => acc.add(USD.fromSerialized(adjustment.amount) ?? USD.fromCents(0)),
          USD.fromCents(0)
        );

        const totalPaidWithAdjustments = totalPaid.add(totalAdjustments);
        const currentDays = calculateDays(timestamps.invoiceDate);
        const overdueDays = calculateDays(timestamps.dueDate);

        return {
          invoice: slug,
          date: parseDate(timestamps.invoiceDate)?.toFormat('MM/dd/yyyy') ?? '--',
          referenceNumber: details.reference,
          paymentTerms: details.paymentTerms,
          dueDate: parseDate(timestamps.dueDate)?.toFormat('MM/dd/yyyy') ?? '--',
          currentDays,
          overdueDays: overdueDays > 0 ? overdueDays : '--',
          amount: formatCurrency(amountDue.dollars, 'USD'),
          payments: formatCurrency(totalPaidWithAdjustments.dollars, 'USD'),
          openBalance: formatCurrency(amountDue.subtract(totalPaidWithAdjustments).dollars, 'USD'),
          openBalanceDollars: amountDue.subtract(totalPaidWithAdjustments).dollars,
          totalPaymentsDollars: totalPaidWithAdjustments.dollars,
        };
      });

    const totalPaid = allInvoices.reduce((acc, invoice) => acc + invoice.totalPaymentsDollars, 0);
    const totalDue = allInvoices.reduce((acc, invoice) => acc + invoice.openBalanceDollars, 0);

    const primaryBusiness = getPrimaryBusiness(organization);

    return {
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),
      email: globalOptions?._settings?.overrideEmail,
      phoneNumber: primaryBusiness?.phone,
      customer: BusinessEntityComponent.templatize(data.businessEntity),
      businessAddress: AddressComponent.templatize(primaryBusiness),
      invoices: applyListMeta(allInvoices),
      totalPaid: formatCurrency(totalPaid, 'USD'),
      totalDue: formatCurrency(totalDue, 'USD'),
    };
  },
  definitionMap: {
    organization: { label: 'Organization', type: 'object', children: OrganizationComponent.definitionMap },
    phoneNumber: { label: 'Phone Number', type: 'string' },
    email: { label: 'Email', type: 'string' },
    totalDue: { label: 'Total Due', type: 'string' },
    totalPaid: { label: 'Total Paid', type: 'string' },
    businessAddress: { label: 'Business Address', type: 'address', children: AddressComponent.definitionMap },
    customer: { label: 'Customer', type: 'object', children: BusinessEntityComponent.definitionMap },
    invoices: {
      label: 'Invoices',
      type: 'list',
      children: {
        invoice: { label: 'Invoice', type: 'string' },
        date: { label: 'Date', type: 'string' },
        referenceNumber: { label: 'Reference Number', type: 'string' },
        dueDate: { label: 'Due Date', type: 'string' },
        currentDays: { label: 'Current Days', type: 'number' },
        overdueDays: { label: 'Overdue Days', type: 'number' },
        amount: { label: 'Amount', type: 'string' },
        payments: { label: 'Payments', type: 'string' },
        openBalance: { label: 'Open Balance', type: 'string' },
      },
    },
  },
};
